import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, MapPinned, Ban, Navigation2, Map as MapIcon } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { FaqSection } from "@/components/FaqSection";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findRegion, REGIONS, REGION_PATH } from "@/lib/regions";
import { DEPARTMENTS, DEPT_PATH } from "@/lib/departments";
import { findCity, CITY_PATH } from "@/lib/cities";
import { THEMATIC_PAGES } from "@/lib/thematic";
import { regionFaq } from "@/lib/faq";

export default function RegionPage() {
  const { slug } = useParams();
  const region = findRegion(slug);
  const faqItems = region ? regionFaq(region) : [];

  useSeo(
    region ? `GPS voiture sans permis ${region.prep} — itinéraires 45 km/h | vspo` : "vspo",
    region
      ? `Itinéraires pour voiture sans permis (VSP) ${region.prep} avec vspo : trajets 45 km/h évitant autoroutes et voies rapides dans tous les départements de ${region.name}, navigation vocale et trafic en temps réel.`
      : "",
    region ? `${REGION_PATH}/${region.slug}` : undefined,
    region ? ogImageUrl({ title: region.name, subtitle: "Voiture sans permis" }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "region-jsonld",
    region
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: `VSP ${region.prep}`, item: `${origin}${REGION_PATH}/${region.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: `vspo — GPS voiture sans permis ${region.prep}`,
            url: `${origin}${REGION_PATH}/${region.slug}`,
            applicationCategory: "TravelApplication",
            operatingSystem: "Web",
            inLanguage: "fr-FR",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            areaServed: { "@type": "AdministrativeArea", name: region.name },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  if (!region) return <Navigate to="/" replace />;

  const deptObjs = region.depts.map((s) => DEPARTMENTS.find((d) => d.slug === s)).filter(Boolean);
  const citySlugs = Array.from(new Set(deptObjs.flatMap((d) => d.cities)));
  const cityObjs = citySlugs.map((s) => findCity(s)).filter(Boolean);
  const others = REGIONS.filter((r) => r.slug !== region.slug);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="region-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <span className="text-foreground">{region.name}</span>
        </nav>

        <div className="mb-2 flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Région</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="region-title">
          GPS voiture sans permis {region.prep}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Vous roulez en <strong>voiture sans permis {region.prep}</strong> ? <strong>vspo</strong> calcule
          des itinéraires adaptés aux voiturettes bridées à <strong>45 km/h</strong> dans toute la région
          {" "}<strong>{region.name}</strong>, en évitant automatiquement les autoroutes et les voies rapides
          interdites aux VSP.
        </p>

        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Ban className="h-5 w-5" /></span>
            <h2 className="font-display text-xl font-bold text-foreground">Rouler en VSP {region.prep}</h2>
          </div>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            Dans chaque département de {region.name}, les voies rapides, rocades et voies pour automobiles
            sont interdites aux voitures sans permis. vspo trace un trajet sur le réseau ordinaire, à la
            vitesse réelle d'une voiturette, avec navigation vocale et trafic en temps réel.
          </p>
        </section>

        {deptObjs.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              <MapPinned className="h-5 w-5 text-primary" />
              <h2 className="font-display text-xl font-bold text-foreground">Départements de {region.name}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {deptObjs.map((d) => (
                <Link key={d.slug} to={`${DEPT_PATH}/${d.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary" data-testid={`region-dept-${d.slug}`}>
                  {d.name} ({d.code})
                </Link>
              ))}
            </div>
          </section>
        )}

        {cityObjs.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-base font-bold text-foreground">Villes {region.prep}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {cityObjs.map((c) => (
                <Link key={c.slug} to={`${CITY_PATH}/${c.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted" data-testid={`region-city-${c.slug}`}>
                  {c.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Calculez votre itinéraire {region.prep}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ouvrez vspo et entrez votre départ et votre arrivée pour un trajet adapté à votre voiture sans permis.</p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="region-cta">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        <section className="mt-8">
          <div className="flex flex-wrap gap-2">
            {THEMATIC_PAGES.map((t) => (
              <Link key={t.slug} to={t.path} className="text-sm text-primary hover:underline" data-testid={`region-thematic-${t.slug}`}>{t.kicker}</Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-base font-bold text-foreground">Autres régions</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((r) => (
              <Link key={r.slug} to={`${REGION_PATH}/${r.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary" data-testid={`region-link-${r.slug}`}>
                {r.name}
              </Link>
            ))}
          </div>
        </section>

        <FaqSection items={faqItems} />
      </main>

      <SeoFooter />
    </div>
  );
}
